import React from "react";
import { Layout, Menu, Image } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DashboardOutlined,
  CheckCircleOutlined,
  ShoppingOutlined,
  DollarOutlined,
  FileTextOutlined,
  TeamOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  PhoneOutlined,
} from "@ant-design/icons";

const { Sider } = Layout;

interface NavigationProps {
  onItemClick?: () => void;
}

interface MenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  path: string;
  active?: boolean;
}

const Navigation: React.FC<NavigationProps> = ({ onItemClick }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems: MenuItem[] = [
    {
      key: "dashboard",
      icon: <DashboardOutlined />,
      label: "Dashboard",
      path: "/",
      active: true,
    },
    {
      key: "validate",
      icon: <CheckCircleOutlined />,
      label: "Validate Incentive Code",
      path: "/validate",
    },
    {
      key: "sales",
      icon: <ShoppingOutlined />,
      label: "Sales In Progress",
      path: "/sales",
    },
    {
      key: "reimbursement",
      icon: <DollarOutlined />,
      label: "Reimbursement Requests",
      path: "/reimbursement",
    },
    {
      key: "reports",
      icon: <FileTextOutlined />,
      label: "Reports",
      path: "/reports",
    },
    {
      key: "users",
      icon: <TeamOutlined />,
      label: "Users",
      path: "/users",
    },
    {
      key: "account",
      icon: <UserOutlined />,
      label: "Account",
      path: "/account",
    },
  ];

  const supportItems: MenuItem[] = [
    {
      key: "faqs",
      icon: <QuestionCircleOutlined />,
      label: "FAQs",
      path: "/faqs",
    },
    {
      key: "contact",
      icon: <PhoneOutlined />,
      label: "Contact Us",
      path: "/contact",
    },
  ];

  const handleMenuClick = (item: any) => {
    const menuItem = [...menuItems, ...supportItems].find(
      (mi) => mi.key === item.key,
    );
    if (menuItem) {
      navigate(menuItem.path);
      if (onItemClick) {
        onItemClick();
      }
    }
  };

  const handleLogoClick = () => {
    navigate("/");
    if (onItemClick) {
      onItemClick();
    }
  };

  const selectedKeys =
    location.pathname === "/"
      ? ["dashboard"]
      : [...menuItems, ...supportItems]
          .filter((item) => item.path === location.pathname)
          .map((item) => item.key);

  // Mobile drawer mode - render without Sider wrapper
  if (onItemClick) {
    return (
      <div
        style={{
          backgroundColor: "#FFF",
          height: "100%",
          overflow: "auto",
          padding: "25px 10px 0 10px",
          display: "flex",
          flexDirection: "column",
        }}
        className="mobile-navigation"
      >
        {/* Logo */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "32px",
            cursor: "pointer",
          }}
          onClick={handleLogoClick}
        >
          <Image
            src="/images/logo.png"
            alt="Bike Shop Logo"
            width={130}
            height={130}
            style={{
              borderRadius: "50%",
              objectFit: "cover",
            }}
            preview={false}
          />
        </div>

        {/* Main Navigation */}
        <div
          style={{
            borderRadius: "4px",
            backgroundColor: "#FFF",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              height: "1px",
              backgroundColor: "#CCC",
              marginBottom: "10px",
            }}
          />

          <Menu
            mode="vertical"
            selectedKeys={selectedKeys}
            onClick={handleMenuClick}
            style={{
              border: "none",
              backgroundColor: "transparent",
            }}
          >
            {menuItems.map((item) => {
              const isSelected = selectedKeys.includes(item.key);
              return (
                <Menu.Item
                  key={item.key}
                  icon={item.icon}
                  style={{
                    borderRadius: "4px",
                    margin: "10px 0",
                    height: "44px",
                    lineHeight: "44px",
                    backgroundColor: isSelected
                      ? "rgba(73, 121, 167, 0.1)"
                      : "#FFF",
                    color: isSelected ? "#4979A7" : "#4D4D4D",
                    fontFamily:
                      "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                    fontSize: "18px",
                    fontWeight: "400",
                    letterSpacing: "0.5px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>{item.label}</span>
                  </div>
                </Menu.Item>
              );
            })}
          </Menu>
        </div>

        {/* Support Section */}
        <div
          style={{
            marginTop: "auto",
            marginBottom: "20px",
            borderRadius: "4px",
            backgroundColor: "#FFF",
          }}
        >
          <div
            style={{
              height: "1px",
              backgroundColor: "#CCC",
              marginBottom: "10px",
            }}
          />

          <Menu
            mode="vertical"
            selectedKeys={selectedKeys}
            onClick={handleMenuClick}
            style={{
              border: "none",
              backgroundColor: "transparent",
            }}
          >
            {supportItems.map((item) => {
              const isSelected = selectedKeys.includes(item.key);
              return (
                <Menu.Item
                  key={item.key}
                  icon={item.icon}
                  style={{
                    borderRadius: "4px",
                    margin: "10px 0",
                    height: "44px",
                    lineHeight: "44px",
                    backgroundColor: isSelected
                      ? "rgba(73, 121, 167, 0.1)"
                      : "#FFF",
                    color: isSelected ? "#4979A7" : "#4D4D4D",
                    fontFamily:
                      "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                    fontSize: "18px",
                    fontWeight: "400",
                    letterSpacing: "0.5px",
                  }}
                >
                  {item.label}
                </Menu.Item>
              );
            })}
          </Menu>
        </div>
      </div>
    );
  }

  // Desktop mode - render with Sider wrapper
  return (
    <Sider
      width={300}
      style={{
        backgroundColor: "#FFF",
        borderRight: "1px solid #CCC",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 100,
        overflow: "auto",
      }}
      className="navigation-sider"
      breakpoint="lg"
      collapsedWidth="0"
    >
      <div
        style={{
          padding: "25px 10px 0 10px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Logo */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "32px",
            cursor: "pointer",
          }}
          onClick={handleLogoClick}
        >
          <Image
            src="/images/logo.png"
            alt="Bike Shop Logo"
            width={130}
            height={130}
            style={{
              borderRadius: "50%",
              objectFit: "cover",
            }}
            preview={false}
          />
        </div>

        {/* Main Navigation */}
        <div
          style={{
            borderRadius: "4px",
            backgroundColor: "#FFF",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              height: "1px",
              backgroundColor: "#CCC",
              marginBottom: "10px",
            }}
          />

          <Menu
            mode="vertical"
            selectedKeys={selectedKeys}
            onClick={handleMenuClick}
            style={{
              border: "none",
              backgroundColor: "transparent",
            }}
          >
            {menuItems.map((item) => {
              const isSelected = selectedKeys.includes(item.key);
              return (
                <Menu.Item
                  key={item.key}
                  icon={item.icon}
                  style={{
                    borderRadius: "4px",
                    margin: "10px 0",
                    height: "44px",
                    lineHeight: "44px",
                    backgroundColor: isSelected
                      ? "rgba(73, 121, 167, 0.1)"
                      : "#FFF",
                    color: isSelected ? "#4979A7" : "#4D4D4D",
                    fontFamily:
                      "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                    fontSize: "18px",
                    fontWeight: "400",
                    letterSpacing: "0.5px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>{item.label}</span>
                  </div>
                </Menu.Item>
              );
            })}
          </Menu>
        </div>

        {/* Support Section */}
        <div
          style={{
            marginTop: "auto",
            marginBottom: "20px",
            borderRadius: "4px",
            backgroundColor: "#FFF",
          }}
        >
          <div
            style={{
              height: "1px",
              backgroundColor: "#CCC",
              marginBottom: "10px",
            }}
          />

          <Menu
            mode="vertical"
            selectedKeys={selectedKeys}
            onClick={handleMenuClick}
            style={{
              border: "none",
              backgroundColor: "transparent",
            }}
          >
            {supportItems.map((item) => {
              const isSelected = selectedKeys.includes(item.key);
              return (
                <Menu.Item
                  key={item.key}
                  icon={item.icon}
                  style={{
                    borderRadius: "4px",
                    margin: "10px 0",
                    height: "44px",
                    lineHeight: "44px",
                    backgroundColor: isSelected
                      ? "rgba(73, 121, 167, 0.1)"
                      : "#FFF",
                    color: isSelected ? "#4979A7" : "#4D4D4D",
                    fontFamily:
                      "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                    fontSize: "18px",
                    fontWeight: "400",
                    letterSpacing: "0.5px",
                  }}
                >
                  {item.label}
                </Menu.Item>
              );
            })}
          </Menu>
        </div>
      </div>
    </Sider>
  );
};

export default Navigation;
